import { ImageView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';

type StreakCloudRowOpts = {
  superview: View;
  width: number;
  variant?: StreakCloudVariant;
  gap?: number;
  imgScale?: number;
  speed?: number;
  opacity?: number;
  offset?: number;
  direction?: -1 | 1;
} & ViewOpts;

const cloudConfigs = {
  cloud1: {
    asset: 'cloud_1',
    width: 641,
    height: 132,
  },
  cloud2: {
    asset: 'cloud_2',
    width: 1337,
    height: 323,
  },
  cloud3: {
    asset: 'cloud_3',
    width: 800,
    height: 300,
  },
} as const;

export type StreakCloudVariant = keyof typeof cloudConfigs;

type StreakCloud = {
  view: ImageView;
  x: number;
};

export default class StreakCloudRow extends View {
  private clouds: StreakCloud[] = [];
  private cloudWidth: number;
  private maxWidth: number;
  private speed: number;
  private direction: number;
  private gap: number;
  private baseOffset: number;
  public offset: number = 0;
  private isAnimating = false;

  constructor(opts: StreakCloudRowOpts) {
    const scale = opts.imgScale ?? 1;
    const config = cloudConfigs[opts.variant ?? 'cloud1'];
    const height = config.height * scale;

    super({
      ...opts,
      height: height,
      y: (opts.y ?? 0) - height * 0.5,
    });

    this.gap = opts.gap ?? 10;
    this.speed = opts.speed ?? 1;
    this.direction = opts.direction ?? -1;
    this.baseOffset = opts.offset ?? 0;
    this.cloudWidth = config.width * scale;
    const amount = Math.ceil(opts.width / (this.cloudWidth + this.gap)) + 1;

    for (let i = 0; i < amount; i++) {
      const x = i * (this.cloudWidth + this.gap) + this.baseOffset;

      this.clouds.push({
        view: new ImageView({
          superview: this,
          image: streakBattleRoyaleTheme().getAsset(config.asset),
          width: this.cloudWidth,
          height,
          x,
        }),
        x,
      });
    }

    this.maxWidth = (this.cloudWidth + this.gap) * amount;
    this.start();
  }

  public start() {
    this.isAnimating = true;
  }

  public stop() {
    this.isAnimating = false;
  }

  public tick(dt: number) {
    if (!this.isAnimating) return;
    const normalisedOffset = this.cloudWidth + this.gap;

    this.clouds.forEach((cloud) => {
      cloud.x += this.direction * this.speed * dt * 0.05;
      cloud.view.style.x = cloud.x + this.offset;

      // normalise
      cloud.view.style.x += normalisedOffset;

      // apply constraint
      cloud.view.style.x =
        ((cloud.view.style.x % this.maxWidth) + this.maxWidth) % this.maxWidth;

      // de-normalise
      cloud.view.style.x -= normalisedOffset;
    });
  }
}
